import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from './api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Plus, Trash2, Pencil, Search, Circle, CheckCircle2, Users } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { errorMessage } from './errors';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import PageContainer from './kit/PageContainer';
import DialogShell from './kit/DialogShell';
import useOpenNewFromUrl from './kit/useOpenNewFromUrl';
import { BRAND } from './kit/tokens';
import { useAuth } from './AuthContext';
import { OwnerChip, OwnerAvatar, OwnerFilter, OwnerSelect, useStaff, ownerParam, groupByOwner } from './kit/OwnerChip';
import { formatDueDate, fromApi, isDateOnly, dueState } from './kit/time';

const prio = { low: '#94a3b8', medium: BRAND.accent, high: '#ef4444' };

const PAGE_SIZE = 50;
const PAGE_MAX = 200; // the server's cap on `limit`
const DONE_WINDOW_DAYS = 30; // Completed shows this much history until "Show older"
const SEARCH_PAUSE_MS = 300;
const SEARCH_MAX = 100; // the server's cap on `search`
const SORTS = [['due', 'Due date'], ['priority', 'Priority'], ['newest', 'Newest']];
const PRIORITIES = [['low', 'Low'], ['medium', 'Medium'], ['high', 'High']];
const EMPTY_COL = { items: [], total: 0 };

// <input type="date"> value for a stored due date: the UTC day when it was
// saved as a date (every task made here), else the viewer's local day.
const dateInputValue = (iso) => {
  if (!iso) return '';
  const d = fromApi(iso);
  if (isDateOnly(d)) return d.toISOString().slice(0, 10);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// The task as the page shows it right after a complete / reopen toggle.
// Either way it leaves its column (a cancelled one is brought back by the
// PATCH) and lands at the top of Open or Completed.
const columnOf = (t) => (t.cancelled_at ? 'cancelled' : (t.completed ? 'done' : 'open'));
const moveTask = (cols, row, from, to) => ({
  ...cols,
  [from]: { items: cols[from].items.filter((x) => x.task_id !== row.task_id), total: Math.max(0, cols[from].total - 1) },
  [to]: { items: [row, ...cols[to].items], total: cols[to].total + 1 },
});
const withTaskMoved = (cols, t, completed) => moveTask(
  cols, { ...t, completed, cancelled_at: null, cancelled_reason: null }, columnOf(t), completed ? 'done' : 'open',
);
// Undo of the move above for one task only, so a failed toggle never undoes
// another toggle made since. A reload in between may already have put it back.
const withTaskPutBack = (cols, t, completed) => {
  const now = completed ? 'done' : 'open';
  if (!cols[now].items.some((x) => x.task_id === t.task_id)) return cols;
  return moveTask(cols, t, now, columnOf(t));
};

export function CancelledPill({ reason }) {
  const label = reason === 'closed_lost' ? 'cancelled · lost' : 'cancelled';
  return (
    <span className="ml-2 inline-block align-middle text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 bg-slate-100 text-slate-500" data-testid="cancelled-pill">
      {label}
    </span>
  );
}

export default function TasksPage() {
  // Ticket 116: owner chip / filter / reassign.
  const { user } = useAuth();
  const me = user?.user_id;
  const perms = user?.permissions || [];
  const canViewAll = perms.includes('loans.view_all');
  // Mirrors the server's write rule (owner_write_filter): your own tasks, or
  // anyone's you can see with `loans.edit`.
  const canEditOthers = perms.includes('loans.edit');
  const staff = useStaff();
  const [ownerFilter, setOwnerFilter] = useState(canViewAll ? 'all' : 'mine');
  const [groupOwners, setGroupOwners] = useState(false);

  const emptyForm = () => ({ title: '', description: '', due_date: '', priority: 'medium', owner_id: me });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [titleError, setTitleError] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);

  // Ticket 194: each column is its own server query (paged, with a true total),
  // so search, filter and sort cover every task, not just the loaded ones.
  // Ticket 196: a global-search hit lands here as ?q=<title>, already searched.
  const [params] = useSearchParams();
  const urlSearch = (params.get('q') || '').slice(0, SEARCH_MAX);
  const [search, setSearch] = useState(urlSearch);
  const [query, setQuery] = useState(urlSearch.trim());
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sort, setSort] = useState('due');
  const [showOlder, setShowOlder] = useState(false);
  const [cols, setCols] = useState({ open: EMPTY_COL, done: EMPTY_COL, cancelled: EMPTY_COL });
  const [loadingMore, setLoadingMore] = useState(null);
  const colsRef = useRef(cols);
  colsRef.current = cols;
  const seq = useRef(0); // a newer load makes an older one's answer stale
  const applied = useRef(0); // counts reloads that replaced the columns
  // Loads read the filters through a ref: a reload that follows a slow save
  // must ask with the filters on screen now, not the ones from the click.
  const filters = { showOlder, showCancelled, ownerFilter, query, priorityFilter, sort };
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), SEARCH_PAUSE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchColumn = async (kind, skip, limit) => {
    const f = filtersRef.current;
    const byKind = {
      open: { completed: false },
      done: { completed: true, ...(f.showOlder ? {} : { completed_within_days: DONE_WINDOW_DAYS }) },
      cancelled: { cancelled_only: 1 },
    };
    const params = {
      ...byKind[kind],
      ...ownerParam(f.ownerFilter, me),
      ...(f.query ? { search: f.query } : {}),
      ...(f.priorityFilter !== 'all' ? { priority: f.priorityFilter } : {}),
      sort: f.sort,
      skip,
      limit,
    };
    const { data } = await api.get('/tasks', { params });
    return data;
  };
  // The first `count` rows of a column, in as many requests as the server's
  // page cap needs.
  const fetchFirst = async (kind, count) => {
    let items = [];
    let total = 0;
    while (items.length < count) {
      const page = await fetchColumn(kind, items.length, Math.min(count - items.length, PAGE_MAX));
      total = page.total;
      items = items.concat(page.items);
      if (!page.items.length || items.length >= total) break;
    }
    return { items, total };
  };
  // `reset` goes back to the first page (a filter changed); otherwise each
  // column is re-asked for as many rows as it is showing.
  const reload = async ({ reset = false } = {}) => {
    const id = ++seq.current;
    const kinds = filtersRef.current.showCancelled ? ['open', 'done', 'cancelled'] : ['open', 'done'];
    try {
      const pages = await Promise.all(kinds.map((k) => {
        const shown = colsRef.current[k].items.length;
        return fetchFirst(k, reset ? PAGE_SIZE : Math.max(shown, PAGE_SIZE));
      }));
      if (id !== seq.current) return;
      applied.current += 1;
      setCols((prev) => {
        const next = { ...prev };
        kinds.forEach((k, i) => { next[k] = { items: pages[i].items, total: pages[i].total }; });
        return next;
      });
    } catch (e) {
      if (id === seq.current) toast({ title: 'Could not load tasks', description: errorMessage(e) });
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload({ reset: true }); }, [showCancelled, ownerFilter, query, priorityFilter, sort, showOlder]);

  const loadMore = async (kind) => {
    // Stale when a reload started, or one already running replaced the column,
    // since this asked: its `skip` was counted against the old rows.
    const id = seq.current;
    const gen = applied.current;
    setLoadingMore(kind);
    try {
      const page = await fetchColumn(kind, colsRef.current[kind].items.length, PAGE_SIZE);
      if (id !== seq.current || gen !== applied.current) return;
      setCols((prev) => {
        const have = new Set(prev[kind].items.map((t) => t.task_id));
        return { ...prev, [kind]: { items: [...prev[kind].items, ...page.items.filter((t) => !have.has(t.task_id))], total: page.total } };
      });
    } catch (e) {
      toast({ title: 'Could not load more tasks', description: errorMessage(e) });
    } finally {
      setLoadingMore(null);
    }
  };

  const save = async () => {
    const title = form.title.trim();
    if (!title) { setTitleError(true); toast({ title: 'Title is required' }); return; }
    try {
      const { owner_id, ...rest } = form;
      await api.post('/tasks', { ...rest, title, due_date: form.due_date ? new Date(form.due_date).toISOString() : null, ...(owner_id ? { owner_id } : {}) });
      toast({ title: 'Task created' }); setOpen(false); setForm(emptyForm()); reload();
    } catch (e) { toast({ title: 'Create failed', description: errorMessage(e) }); }
  };
  const openNew = () => { setTitleError(false); setOpen(true); };
  useOpenNewFromUrl(openNew); // the top-bar + menu links here with ?new=1

  const startEdit = (t) => {
    setTitleError(false);
    setEditForm({ title: t.title || '', description: t.description || '', due_date: dateInputValue(t.due_date), priority: t.priority || 'medium' });
    setEditing(t);
  };
  // Sends only what changed: a due date that carries a time (not made here)
  // is never flattened to midnight by an edit of something else.
  const saveEdit = async () => {
    const title = editForm.title.trim();
    if (!title) { setTitleError(true); toast({ title: 'Title is required' }); return; }
    const patch = {};
    if (title !== editing.title) patch.title = title;
    const description = editForm.description.trim() ? editForm.description : null;
    if (description !== (editing.description || null)) patch.description = description;
    if (editForm.priority !== editing.priority) patch.priority = editForm.priority;
    if (editForm.due_date !== dateInputValue(editing.due_date)) {
      patch.due_date = editForm.due_date ? new Date(editForm.due_date).toISOString() : null;
    }
    if (!Object.keys(patch).length) { setEditing(null); return; }
    try {
      await api.patch(`/tasks/${editing.task_id}`, patch);
      toast({ title: 'Task updated' }); setEditing(null); reload();
    } catch (e) { toast({ title: 'Save failed', description: errorMessage(e) }); }
  };
  // Only rows the server will let this user change get the control; a GHL
  // mirror never does (write-through is not built, so the PATCH is a 409).
  const canEdit = (t) => t.source !== 'ghl' && (t.owner_id === me || canEditOthers);

  // A reassign that takes the task out of this list gets a confirm: an LO
  // can't see (or undo) a task once it's someone else's.
  const leavesView = (ownerId) => {
    if (!canViewAll) return ownerId !== me;
    if (ownerFilter === 'all') return false;
    return ownerId !== (ownerFilter === 'mine' ? me : ownerFilter);
  };
  const reassign = async (t, ownerId) => {
    if (leavesView(ownerId)) {
      const name = staff.find((m) => m.user_id === ownerId)?.name || 'this teammate';
      const ok = await confirmDialog({
        title: `Reassign to ${name}?`,
        description: canViewAll
          ? 'It will leave this filtered list. Switch the owner filter to Everyone to find it again.'
          : `It will leave your task list, and you won't be able to see it or move it back. ${name} or an admin can.`,
        confirmText: 'Reassign',
      });
      if (!ok) return;
    }
    try {
      const { data } = await api.post(`/tasks/${t.task_id}/owner`, { owner_id: ownerId });
      toast({ title: `Reassigned to ${data?.owner_name || 'teammate'}` });
      reload();
    } catch (e) { toast({ title: 'Reassign failed', description: errorMessage(e) }); }
  };
  // The row moves at once; a failed PATCH puts it back and says why.
  const toggle = async (t) => {
    const completed = !t.completed;
    setCols((cur) => withTaskMoved(cur, t, completed));
    try {
      await api.patch(`/tasks/${t.task_id}`, { completed });
    } catch (e) {
      setCols((cur) => withTaskPutBack(cur, t, completed));
      toast({ title: 'Update failed', description: errorMessage(e) });
      return;
    }
    reload();
  };
  const del = async (id) => {
    if (!(await confirmDialog({ title: 'Delete task?', confirmText: 'Delete', destructive: true }))) return;
    try { await api.delete(`/tasks/${id}`); reload(); }
    catch (e) { toast({ title: 'Delete failed', description: errorMessage(e) }); }
  };

  const column = (kind, title, extra = {}) => (
    <TaskColumn
      kind={kind}
      title={title}
      col={cols[kind]}
      onToggle={toggle}
      onDelete={del}
      onReassign={reassign}
      onEdit={startEdit}
      canEdit={canEdit}
      onLoadMore={() => loadMore(kind)}
      loadingMore={loadingMore === kind}
      staff={staff}
      groupOwners={groupOwners}
      {...extra}
    />
  );

  return (
    <PageContainer
      title="Tasks"
      subtitle={`${cols.open.total} open • ${cols.done.total} completed${showOlder ? '' : ` in the last ${DONE_WINDOW_DAYS} days`}`}
      actions={(
        <div className="flex items-center gap-2 flex-wrap">
          <OwnerFilter value={ownerFilter} onChange={setOwnerFilter} staff={staff} canViewAll={canViewAll} testId="tasks-owner-filter" />
          {canViewAll && (
            <Button variant={groupOwners ? 'accent' : 'outline-navy'} onClick={() => setGroupOwners(v => !v)} aria-pressed={groupOwners} data-testid="tasks-group-owners" title="Group tasks by owner">
              <Users size={14} /> By owner
            </Button>
          )}
          <Button variant="outline-navy" onClick={() => setShowCancelled(v => !v)} data-testid="toggle-cancelled">
            {showCancelled ? 'Hide cancelled' : 'Show cancelled'}
          </Button>
          <Button variant="accent" onClick={openNew}><Plus size={16} /> New Task</Button>
        </div>
      )}
    >
      <div className="flex items-center gap-2 flex-wrap" data-testid="tasks-toolbar">
        <div className="relative w-full sm:w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} maxLength={SEARCH_MAX} placeholder="Search title or description" aria-label="Search tasks" data-testid="tasks-search" className="pl-9" />
        </div>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-44" aria-label="Filter by priority" data-testid="tasks-priority-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {PRIORITIES.map(([v, label]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500 ml-1">Sort by</span>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-36" aria-label="Sort tasks" data-testid="tasks-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            {SORTS.map(([v, label]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className={`grid gap-6 ${showCancelled ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
        {column('open', 'Open')}
        {column('done', 'Completed', { done: true, windowDays: showOlder ? null : DONE_WINDOW_DAYS, onToggleOlder: () => setShowOlder((v) => !v) })}
        {showCancelled && column('cancelled', 'Cancelled', { cancelled: true })}
      </div>

      <DialogShell open={open} onOpenChange={setOpen} title="New Task" aria-describedby={undefined}>
        <TaskFormFields form={form} setForm={setForm} titleError={titleError} clearTitleError={() => setTitleError(false)}>
          <div><Label>Owner</Label>
            <OwnerSelect value={form.owner_id || ''} staff={staff} onChange={(v) => setForm({...form, owner_id: v})} className="mt-1" testId="task-new-owner" />
          </div>
        </TaskFormFields>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline-navy" onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="accent" onClick={save}>Create</Button>
        </div>
      </DialogShell>

      <DialogShell open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }} title="Edit task" aria-describedby={undefined}>
        {editing && (
          <>
            {editing.cancelled_at && (
              <p className="text-xs text-slate-500 mb-3" data-testid="task-edit-cancelled-note">Saving changes brings this cancelled task back.</p>
            )}
            <TaskFormFields form={editForm} setForm={setEditForm} titleError={titleError} clearTitleError={() => setTitleError(false)} />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline-navy" onClick={() => setEditing(null)}>Cancel</Button>
              <Button variant="accent" onClick={saveEdit} data-testid="task-edit-save">Save</Button>
            </div>
          </>
        )}
      </DialogShell>
    </PageContainer>
  );
}

// Title, description, due date and priority: the fields the create and edit
// dialogs share. `children` slots in between (the owner picker, create only).
function TaskFormFields({ form, setForm, titleError, clearTitleError, children }) {
  return (
    <div className="space-y-3">
      <div>
        <Label>Title *</Label>
        <Input
          value={form.title}
          onChange={(e) => { setForm({ ...form, title: e.target.value }); if (titleError) clearTitleError(); }}
          aria-invalid={titleError}
          data-testid="task-form-title"
          className={`mt-1 ${titleError ? 'border-red-500' : ''}`}
        />
      </div>
      <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-form-description" className="mt-1" /></div>
      {children}
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-form-due" className="mt-1" /></div>
        <div><Label>Priority</Label>
          <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
            <SelectTrigger className="mt-1" data-testid="task-form-priority"><SelectValue /></SelectTrigger>
            <SelectContent>{PRIORITIES.map(([v, label]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

// What a task is for: its lead (when the caller can read it) or its contact.
// Neither → a plain to-do, no link.
function taskFor(t) {
  if (t.lead_name) return { kind: 'Lead', name: t.lead_name, to: `/portal/leads/${t.lead_id}` };
  if (t.contact_name) return { kind: 'Contact', name: t.contact_name, to: `/portal/contacts/${t.contact_id}` };
  return null;
}

// An empty slot the size of an icon button, so owner chips line up on rows
// with no edit or delete control.
const Spacer = () => <span className="p-1.5" aria-hidden="true"><span className="block w-[13px] h-[13px]" /></span>;

const DUE_CUE = {
  overdue: { label: 'Overdue', text: 'text-red-600 font-medium', pill: 'bg-red-50 text-red-700' },
  today: { label: 'Due today', text: 'text-amber-600 font-medium', pill: 'bg-amber-50 text-amber-700' },
};

function TaskColumn({ kind, title, col, onToggle, onDelete, onReassign, onEdit, canEdit, onLoadMore, loadingMore, staff = [], groupOwners = false, windowDays, onToggleOlder, done, cancelled }) {
  const tasks = col.items;
  const groups = groupOwners ? groupByOwner(tasks, staff) : null;
  // Ticket 135 S3: GHL-mirrored tasks are read-only here — GHL owns them.
  // Ticket 231: the one exception is completing an open one, which the server
  // writes to GHL first (`ghl_completable`); reopening stays in GHL.
  const renderTask = (t) => {
    const ghl = t.source === 'ghl';
    const locked = ghl && !(t.ghl_completable && !t.completed);
    const target = taskFor(t);
    // Overdue / due-today only mean something on a task that is still open.
    const state = !t.completed && !t.cancelled_at ? dueState(t.due_date) : null;
    const cue = state ? DUE_CUE[state] : null;
    return (
          <div key={t.task_id} className="flex items-start gap-3 p-3 rounded-xl hover:bg-slate-50 group" data-testid={`task-row-${t.task_id}`} data-due-state={state || undefined}>
            <button
              onClick={locked ? undefined : () => onToggle(t)}
              disabled={locked}
              title={locked ? (t.completed ? 'Reopen it in GoHighLevel' : 'Complete it in GoHighLevel') : (ghl ? 'Completes it in GoHighLevel too' : undefined)}
              className={`mt-0.5 ${locked ? 'cursor-not-allowed opacity-60' : ''}`}
              aria-label={t.completed ? `Reopen task: ${t.title}` : `Complete task: ${t.title}`}
            >
              {t.completed ? <CheckCircle2 size={20} className="text-brand-accent" /> : <Circle size={20} className="text-slate-300 hover:text-slate-500" />}
            </button>
            <div className="flex-1 min-w-0">
              <div className={`font-medium text-sm ${done || cancelled ? 'line-through text-slate-400' : 'text-brand-navy'}`}>
                {target ? <Link to={target.to} className="hover:underline" data-testid={`task-link-${t.task_id}`}>{t.title}</Link> : t.title}
                {cancelled && <CancelledPill reason={t.cancelled_reason} />}
                {ghl && <span className="ml-2 align-middle text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 no-underline" data-testid={`ghl-badge-${t.task_id}`}>GHL</span>}
              </div>
              {target && (
                <div className="text-xs text-slate-500 mt-0.5" data-testid={`task-for-${t.task_id}`}>
                  {target.kind}: <Link to={target.to} className="text-brand-navy underline">{target.name}</Link>
                </div>
              )}
              {t.description && <div className="text-xs text-slate-500 mt-0.5">{t.description}</div>}
              <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                <span className="text-[10px] uppercase font-semibold" style={{ color: prio[t.priority] }}>{t.priority}</span>
                {t.due_date && <span className={`text-xs ${cue ? cue.text : 'text-slate-500'}`}>Due {formatDueDate(t.due_date)}</span>}
                {cue && <span className={`text-[10px] uppercase font-semibold rounded-full px-2 py-0.5 ${cue.pill}`} data-testid={`task-due-${t.task_id}`}>{cue.label}</span>}
                {ghl && <span className="text-xs text-slate-400" data-testid={`task-managed-${t.task_id}`}>Managed in GoHighLevel</span>}
              </div>
            </div>
            {/* Chip, edit and delete share one centred row; rows without a
                control keep an empty slot so every chip lines up. */}
            <div className="flex items-center gap-1 shrink-0">
              {!groupOwners && (
                <span data-testid={`task-owner-${t.task_id}`} className="flex">
                  <OwnerChip userId={t.owner_id} name={t.owner_name} staff={staff} onChange={onReassign && !ghl ? (uid) => onReassign(t, uid) : undefined} stop={false} />
                </span>
              )}
              {canEdit(t) ? (
                <button onClick={() => onEdit(t)} aria-label={`Edit task: ${t.title}`} className="p-1.5 hover:bg-slate-100 rounded"><Pencil size={13} className="text-slate-500" /></button>
              ) : <Spacer />}
              {ghl ? (
                <Spacer />
              ) : (
                <button onClick={() => onDelete(t.task_id)} aria-label={`Delete task: ${t.title}`} className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-50 rounded"><Trash2 size={13} className="text-red-500" /></button>
              )}
            </div>
          </div>
    );
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm" data-testid={`task-col-${kind}`}>
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="font-serif font-semibold text-brand-navy">{title}</div>
        <span className="text-xs bg-slate-100 rounded-full px-2 py-0.5 text-slate-600" data-testid="task-col-count">{col.total}</span>
      </div>
      <div className="p-3 space-y-2 min-h-[240px]">
        {tasks.length === 0 && <div className="text-center text-slate-400 text-sm py-10">Nothing here.</div>}
        {groups ? groups.map((g) => (
          <div key={g.owner_id || 'none'} data-testid={`owner-group-${g.owner_id || 'none'}`}>
            <div className="flex items-center gap-2 px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <OwnerAvatar userId={g.owner_id} name={g.name} size="sm" />
              <span className="text-brand-navy">{g.name}</span>
              <span className="text-slate-400 normal-case tracking-normal font-medium">· {g.rows.length}</span>
            </div>
            {g.rows.map(renderTask)}
          </div>
        )) : tasks.map(renderTask)}
      </div>
      {(col.total > 0 || onToggleOlder) && (
        <div className="px-5 pb-4 pt-1 space-y-1.5 text-xs text-slate-500">
          {col.total > 0 && (
            <div className="flex items-center justify-between gap-2">
              <span data-testid="task-col-showing">Showing {tasks.length} of {col.total}</span>
              {tasks.length < col.total && (
                <Button variant="outline-navy" size="sm" onClick={onLoadMore} disabled={loadingMore}>Load more</Button>
              )}
            </div>
          )}
          {onToggleOlder && (
            <div className="flex items-center justify-between gap-2">
              <span data-testid="task-col-window">{windowDays ? `Last ${windowDays} days` : 'All time'}</span>
              <button type="button" onClick={onToggleOlder} className="underline text-brand-navy">
                {windowDays ? 'Show older' : `Last ${DONE_WINDOW_DAYS} days only`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
